import { useNotifications } from '@toolpad/core';
import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { BusinessLocker } from '@barboxx/models';

import { generateLockerNfcPickupUrls, LockerNfcUrl } from './nfcCredentials';

// Generated by Copilot

interface Props {
    open: boolean;
    locker: BusinessLocker | null;
    onClose: () => void;
}

export function LockerNfcUrlsDialog({ open, locker, onClose }: Props) {
    const notifications = useNotifications();
    const [urls, setUrls] = React.useState<LockerNfcUrl[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    React.useEffect(() => {
        let isMounted = true;

        const loadUrls = async () => {
            if (!open || !locker) {
                return;
            }

            try {
                setLoading(true);
                setErrorMessage(null);

                const generatedUrls = await generateLockerNfcPickupUrls({
                    businessId: locker.businessId,
                    lockerId: locker.id,
                    numberOfCubbies: locker.numberOfCubbies,
                    nfcSecret: locker.nfcSecret,
                });

                if (isMounted) {
                    setUrls(generatedUrls);
                }
            } catch (error) {
                if (isMounted) {
                    setUrls([]);
                    setErrorMessage(
                        error instanceof Error
                            ? error.message
                            : 'Failed to generate NFC URLs.'
                    );
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        void loadUrls();

        return () => {
            isMounted = false;
        };
    }, [locker, open]);

    const copyToClipboard = React.useCallback(
        async (text: string, successMessage: string) => {
            try {
                await window.navigator.clipboard.writeText(text);
                notifications.show(successMessage, {
                    severity: 'success',
                    autoHideDuration: 1000,
                });
            } catch (_error) {
                notifications.show('Could not copy to clipboard.', {
                    severity: 'error',
                    autoHideDuration: 1200,
                });
            }
        },
        [notifications]
    );

    const handleCopyAll = React.useCallback(() => {
        const allUrls = urls.map(({ url }) => url).join('\n');

        if (!allUrls) {
            return;
        }

        void copyToClipboard(allUrls, 'Copied all NFC URLs');
    }, [copyToClipboard, urls]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                NFC Pickup URLs{locker ? ` · Locker ${locker.name}` : ''}
            </DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <Typography variant="body2">
                        Copy each URL into your NFC writer app and program one
                        tag per cubby. Each sticker can contain all three
                        environment URLs as separate NDEF records.
                    </Typography>

                    {loading && <Typography>Generating URLs...</Typography>}

                    {errorMessage && (
                        <Typography color="error">{errorMessage}</Typography>
                    )}

                    {!loading &&
                        !errorMessage &&
                        urls.map(({ cubbyId, url }) => {
                            const cubbyNumber = cubbyId + 1;
                            return (
                                <Stack key={cubbyId} direction="row" gap={1}>
                                    <TextField
                                        label={`Cubby ${cubbyNumber}`}
                                        value={url}
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                            },
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            void copyToClipboard(
                                                url,
                                                `Copied cubby ${cubbyNumber} URL`
                                            )
                                        }
                                    >
                                        Copy
                                    </Button>
                                </Stack>
                            );
                        })}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
                <Button
                    onClick={handleCopyAll}
                    variant="contained"
                    disabled={loading || !!errorMessage || urls.length === 0}
                >
                    Copy All
                </Button>
            </DialogActions>
        </Dialog>
    );
}
